// Global Instructions Rule Applied!
//
// Waterfall position section of the fund's Statements tab
// (docs/WATERFALL_CARRY_DESIGN.md, phase 4): where each partner stands in the
// fund's distribution waterfall as at a date - what it contributed and
// received, the carry it has paid, what is left of each test - and the
// hypothetical liquidation of its capital account at fair market value: the
// accrued carried interest its statement states.
//
// One read (wf__hypothetical_liquidation through GET /funds/:id/waterfall/
// position). Three answers besides figures: no open waterfall (the fund's
// statements stay before carry), a legal entity of a consolidated fund (its
// position is read with the consolidated fund - no figures here), and a
// waterfall the engine cannot run (its own sentence, as an error).
//
// SHARED with the consolidated fund's Statements tab (waterfall phase 5): in
// group mode (`group`) the read is GET /fund-groups/:id/waterfall/position -
// the same model over the consolidated fund's partners.
//
// Two kinds of row stand outside the limited partners: the GP commitment (the
// general partner's own commitment - its share is paid as such, it bears no
// carry) and the row that receives the carried interest. In most funds they
// are one row; a fund whose carry has a commitment of its own shows two, the
// GP commitment with its own share and no carried interest, the carry's row
// last.
//
// A waterfall with pools (phase 6 PR 3): an investor's row opens to its tiers
// and, under them, its pools - what it contributed in each pool, what the pool
// returned, the loss offsets it received from other pools and paid to them,
// the capital still unreturned and the pool's realized loss not yet offset.
// Unreturned capital on the row is the sum over the pools.
//
// Section-local read on the Statement Runs section's recipe: a hook, no
// redux slice.

import React, { useMemo, useState } from 'react'
import { Alert, Button, Table, Tag, Typography } from 'antd'
import DatePicker from '../../../../../core/components/DatePicker.tsx'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import NumberText from '../../../../../core/components/NumberText.tsx'
import { lastCompletedQuarterEnd } from '../../../../../core/utils/quarterEnds.ts'
import { useFundWaterfallPosition } from '../../../hooks/useFundWaterfallPosition.ts'
import type {
  WaterfallPositionPartner,
  WaterfallPositionPool,
  WaterfallPositionTotals
} from '../../../../../state_manager/data_models/fund_waterfall_position_record.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const ISO_DATE = 'YYYY-MM-DD'

// "1.25x": how many times its contributions a partner has received
const multiple = (v: number | null | undefined): string => (v == null ? '—' : `${v.toFixed(2)}x`)

const money = (v: number | null | undefined): React.ReactNode => <NumberText value={v} />

// one row of the by-tier table: a tier, what it has paid and what it would pay
interface TierRow {
  tier_sequence: number
  tier_type: string
  received_lp: number
  received_gp: number
  hypothetical_lp: number
  hypothetical_gp: number
}

const tierRows = (partner: WaterfallPositionPartner): TierRow[] => {
  const rows = new Map<number, TierRow>()
  const row = (sequence: number, type: string): TierRow => {
    const existing = rows.get(sequence)
    if (existing) return existing
    const fresh: TierRow = {
      tier_sequence: sequence,
      tier_type: type,
      received_lp: 0,
      received_gp: 0,
      hypothetical_lp: 0,
      hypothetical_gp: 0
    }
    rows.set(sequence, fresh)
    return fresh
  }
  partner.by_tier.forEach((t) => {
    const r = row(t.tier_sequence, t.tier_type)
    r.received_lp = t.lp
    r.received_gp = t.gp
  })
  partner.hypothetical_by_tier.forEach((t) => {
    const r = row(t.tier_sequence, t.tier_type)
    r.hypothetical_lp = t.lp
    r.hypothetical_gp = t.gp
  })
  return Array.from(rows.values()).sort((a, b) => a.tier_sequence - b.tier_sequence)
}

// not an investor's row: the GP commitment, the row that receives the carry
const outsideInvestors = (row: WaterfallPositionPartner): boolean => row.is_gp_commitment || row.is_carry_recipient

// what the totals row leaves out, as the note under the table names it
const leftOutOfTotal = (partners: WaterfallPositionPartner[]): string => {
  const carryRowApart = partners.some((p) => p.is_carry_recipient && !p.is_gp_commitment)
  if (!carryRowApart) return 'the GP commitment'
  return partners.some((p) => p.is_gp_commitment)
    ? 'the GP commitment and the commitment that receives the carried interest'
    : 'the commitment that receives the carried interest'
}

const TIER_COLUMNS: ColumnsType<TierRow> = [
  { title: 'Tier', dataIndex: 'tier_sequence', width: 60 },
  { title: 'Type', dataIndex: 'tier_type' },
  { title: 'Received, LP', dataIndex: 'received_lp', width: 150, align: 'right', render: money },
  { title: 'Received, GP', dataIndex: 'received_gp', width: 150, align: 'right', render: money },
  { title: 'Hypothetical, LP', dataIndex: 'hypothetical_lp', width: 150, align: 'right', render: money },
  { title: 'Hypothetical, GP', dataIndex: 'hypothetical_gp', width: 150, align: 'right', render: money }
]

// an investor's part of each pool of a waterfall with pools, oldest first
const POOL_COLUMNS: ColumnsType<WaterfallPositionPool> = [
  { title: 'Pool', dataIndex: 'pool_code', width: 90 },
  { title: 'Contributed', dataIndex: 'contributions', width: 130, align: 'right', render: money },
  { title: 'Return of capital', dataIndex: 'capital_returned', width: 140, align: 'right', render: money },
  { title: 'Loss offset received', dataIndex: 'remediation_received', width: 150, align: 'right', render: money },
  { title: 'Loss offset paid', dataIndex: 'remediation_given', width: 140, align: 'right', render: money },
  { title: 'Unreturned capital', dataIndex: 'capital_due', width: 150, align: 'right', render: money },
  {
    // what the partner received from the pool's own proceeds
    title: 'Distributed',
    key: 'distributed',
    width: 130,
    align: 'right',
    render: (_: unknown, p) => money(p.capital_returned + p.remediation_given + p.profit_received)
  },
  { title: 'Carry paid', dataIndex: 'carry_paid', width: 130, align: 'right', render: money },
  { title: 'Loss to offset', dataIndex: 'unremedied_loss', width: 130, align: 'right', render: money }
]

const COLUMNS: ColumnsType<WaterfallPositionPartner> = [
  {
    title: 'Investor',
    key: 'investor',
    render: (_: unknown, row) => (
      <span>
        {row.investor_name ?? '—'}
        {row.is_gp_commitment ? (
          <Tag color='purple' className='ml-2'>
            GP commitment
          </Tag>
        ) : row.is_carry_recipient ? (
          // the carry's own row, apart from the GP commitment
          <Tag color='purple' className='ml-2'>
            Receives carried interest
          </Tag>
        ) : null}
      </span>
    )
  },
  { title: 'Contributed', key: 'contributed', width: 120, align: 'right', render: (_: unknown, row) => money(row.contributions) },
  {
    // the GP commitment: its own share, paid as such (none on a carry row of its own)
    title: 'Distributed',
    key: 'distributed',
    width: 120,
    align: 'right',
    render: (_: unknown, row) => money(outsideInvestors(row) ? row.distributions.own_share : row.distributions.lp)
  },
  {
    // an investor: the carry its profit bore; the row that receives the carry:
    // the carry it received (a GP commitment that is not that row: none)
    title: 'Carry paid',
    key: 'carry_paid',
    width: 120,
    align: 'right',
    render: (_: unknown, row) => money(outsideInvestors(row) ? row.distributions.carry_received : row.carry_paid)
  },
  {
    title: 'Unreturned capital',
    key: 'unreturned',
    width: 120,
    align: 'right',
    render: (_: unknown, row) => money(row.unreturned_capital)
  },
  {
    title: 'Multiple reached',
    key: 'multiple',
    width: 100,
    align: 'right',
    render: (_: unknown, row) => multiple(row.multiple_reached)
  },
  {
    title: 'Catch-up remaining',
    key: 'catch_up',
    width: 120,
    align: 'right',
    render: (_: unknown, row) => (outsideInvestors(row) ? '—' : money(row.catch_up_remaining))
  },
  { title: 'Capital at FMV', key: 'fmv', width: 120, align: 'right', render: (_: unknown, row) => money(row.fmv_capital) },
  {
    // an investor: accrued against it; the row that receives the carry: what
    // it would receive (a GP commitment that is not that row: none)
    title: 'Accrued carry',
    key: 'accrued',
    width: 120,
    align: 'right',
    render: (_: unknown, row) => money(row.accrued_carry)
  },
  {
    title: 'Capital at FMV after carry',
    key: 'after_carry',
    width: 120,
    align: 'right',
    render: (_: unknown, row) => money(row.fmv_capital_after_carry)
  }
]

// the totals row, in the order of the columns after Investor
const totalCells = (totals: WaterfallPositionTotals): React.ReactNode[] => [
  money(totals.contributions),
  money(totals.lp_received),
  money(totals.carry_paid),
  money(totals.unreturned_capital),
  multiple(totals.multiple_reached),
  money(totals.catch_up_remaining),
  money(totals.fmv_capital),
  money(totals.accrued_carry),
  money(totals.fmv_capital_after_carry)
]

interface WaterfallPositionSectionProps {
  fundId?: string | null
  /** Consolidated mode (fund-group form): the consolidated fund's position; `fundId` is ignored. */
  group?: { fundGroupId: string } | null
}

export default function WaterfallPositionSection({ fundId, group }: WaterfallPositionSectionProps) {
  const [asOf, setAsOf] = useState<Dayjs>(() => lastCompletedQuarterEnd())
  const fundGroupId = group?.fundGroupId ?? null
  const { position, loaded, loading, error, refetch } = useFundWaterfallPosition(
    fundGroupId ? { fundGroupId } : { fundId },
    asOf.format(ISO_DATE)
  )

  const totals = position?.totals ?? null
  const summaryCells = useMemo(() => (totals ? totalCells(totals) : []), [totals])

  return (
    <FormSection
      title='Waterfall position'
      titleColor='purple'
      loading={loading}
      className='mt-3'
      headerActions={
        <div className='flex items-center gap-2'>
          <DatePicker
            size='small'
            allowClear={false}
            aria-label='As of'
            value={asOf}
            format={DATE_FORMAT}
            onChange={(d) => {
              if (d) setAsOf(d)
            }}
          />
          <Button size='small' icon={<ReloadOutlined />} onClick={() => void refetch()}>
            Refresh
          </Button>
        </div>
      }
    >
      {error ? (
        <Alert type='error' showIcon message='Failed to load the waterfall position' description={error} />
      ) : !loaded ? null : !position ? (
        <Text type='secondary'>
          {fundGroupId
            ? 'No waterfall. The statements of this consolidated fund state no carried interest until one is set up on the Terms tab of its primary fund.'
            : "No waterfall. This fund's statements state no carried interest until one is set up on the Terms tab."}
        </Text>
      ) : position.consolidated_fund ? (
        <Text type='secondary'>
          A legal entity of {position.consolidated_fund.fund_groups__name ?? 'a consolidated fund'}: its waterfall
          position is read with the consolidated fund.
        </Text>
      ) : (
        <>
          <Table<WaterfallPositionPartner>
            size='small'
            pagination={false}
            rowKey='fund_commitment_id'
            columns={COLUMNS}
            dataSource={position.partners}
            scroll={{ x: 1250 }}
            expandable={{
              // a row outside the investors runs through no tier: nothing to open
              rowExpandable: (row) => !outsideInvestors(row),
              expandedRowRender: (row) => (
                <div className='py-1'>
                  <Table<TierRow>
                    size='small'
                    pagination={false}
                    rowKey='tier_sequence'
                    columns={TIER_COLUMNS}
                    dataSource={tierRows(row)}
                  />
                  {row.pools && row.pools.length > 0 ? (
                    <Table<WaterfallPositionPool>
                      className='mt-2'
                      size='small'
                      pagination={false}
                      rowKey='pool_id'
                      columns={POOL_COLUMNS}
                      dataSource={row.pools}
                    />
                  ) : null}
                </div>
              )
            }}
            summary={() =>
              totals ? (
                <Table.Summary fixed>
                  <Table.Summary.Row className='font-medium'>
                    <Table.Summary.Cell index={0} />
                    <Table.Summary.Cell index={1}>Total, limited partners</Table.Summary.Cell>
                    {summaryCells.map((cell, i) => (
                      <Table.Summary.Cell key={i} index={i + 2} align='right'>
                        {cell}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
          <Text type='secondary' className='block mt-2'>
            Capital at FMV is the capital account plus the share of the unrealized gain as at the date. Accrued carry is
            what the GP would take if the fund were liquidated at those values, after what has already been distributed;
            the total leaves out {leftOutOfTotal(position.partners)}.
            {position.partners.some((p) => p.pools && p.pools.length > 0)
              ? ' This waterfall has pools: unreturned capital is the sum over the pools, each returning its own capital first, and an investor\u2019s row opens to its pools.'
              : ''}
          </Text>
        </>
      )}
    </FormSection>
  )
}
